/**
 * CRM v2 — Bloco C + CA-6: aba "Lembretes" em Configurações › Comercial (CRM).
 *
 * Liga/desliga o lembrete de atividade agendada, escolhe a antecedência do
 * aviso e — CA-6 (mig 413) — administra o CATÁLOGO de tempos de lembrete e o
 * tempo PADRÃO por tipo de atividade. Tipo sem padrão obriga quem cria a
 * atividade a escolher um tempo (a obrigatoriedade na criação é a fase 2).
 * Persiste no SQLite via /api/v1/crm/config/* — nada de estado no navegador.
 */

import { useState } from 'react';

import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  HelpTip,
  Input,
  Label,
  Select,
  SelectOption,
  Switch,
  useToast,
} from '@/components/ui';
import { cn } from '@/lib/cn';
import {
  useLembreteConfig,
  useLembreteConfigActions,
  type LembreteConfig,
} from '@/lib/crm/lembrete-config-store';
import { useConfigActions, useLembreteTempos, useTiposAtividadeCrm } from '@/lib/crm/crm-store';
import { mensagemErroConfig } from './config-crm/erro-config';
import { rotuloMinutos } from '@/lib/crm/lembrete-rotulo';
import type { LembreteTempo } from '@/lib/crm/crm-types';

export function LembreteConfigTab() {
  const configQuery = useLembreteConfig();
  const { salvar } = useLembreteConfigActions();
  const tempos = useLembreteTempos(true);
  const toast = useToast();
  const config = configQuery.data;

  const salvarCampo = async (body: Partial<LembreteConfig>) => {
    try {
      await salvar.mutateAsync(body);
    } catch {
      toast.show('Não foi possível salvar. Tente de novo.', { variant: 'error' });
    }
  };

  if (configQuery.isLoading) {
    return <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>;
  }
  if (!config) {
    return <p className="text-sm text-ink-3 dark:text-ink-dark-3">Não foi possível carregar a configuração.</p>;
  }

  const desligado = !config.ativo;
  const temposAtivos = (tempos.data ?? []).filter((t) => t.ativo);
  // Bug latente (inventário D11) resolvido: valor salvo fora do catálogo
  // continua aparecendo no select, como opção extra.
  const antecedenciaForaDoCatalogo = !temposAtivos.some((t) => t.minutos === config.antecedenciaMin);

  return (
    <div className="space-y-4 min-w-0">
      <Card variant="flat" className="p-4 min-w-0">
        <h3 className="text-base font-bold text-ink-1 dark:text-ink-dark-1 mb-1 inline-flex items-center gap-1.5">
          Lembretes de atividade
          <HelpTip texto="Aviso automático no sininho antes da hora de uma tarefa agendada (com hora) num lead ou proposta. Assim ninguém perde o horário de ligar, visitar ou enviar." />
        </h3>
        <p className="text-sm text-ink-3 dark:text-ink-dark-3 mb-3">
          Quando alguém agenda uma tarefa com data e hora, o responsável recebe um aviso no sininho pouco
          antes do horário, com link direto pro lead ou pra proposta. Só tarefas com hora geram lembrete.
        </p>
        <Switch
          label="Lembretes ligados"
          hint={
            desligado
              ? 'Desligado: nenhuma tarefa agendada gera aviso.'
              : 'Ligado: o responsável é avisado antes da hora de cada tarefa agendada.'
          }
          checked={config.ativo}
          onChange={(e) => void salvarCampo({ ativo: e.target.checked })}
        />
      </Card>

      <div className={cn('grid gap-4 items-start sm:grid-cols-2', desligado && 'opacity-60')}>
        <Card variant="flat" className="p-4 min-w-0">
          <span className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <Label htmlFor="lembrete-antecedencia" className="mb-0">Avisar quanto antes</Label>
            <HelpTip texto="Quanto tempo antes da hora marcada o aviso chega, quando a tarefa não tem um tempo próprio. Ex.: 30 minutos = a tarefa das 14:00 avisa às 13:30." />
          </span>
          <Select
            id="lembrete-antecedencia"
            size="sm"
            value={String(config.antecedenciaMin)}
            disabled={desligado}
            onChange={(e) => void salvarCampo({ antecedenciaMin: Number(e.target.value) })}
            className="mt-1 w-full"
          >
            {temposAtivos.map((t) => (
              <SelectOption key={t.id} value={String(t.minutos)}>
                {t.nome}
              </SelectOption>
            ))}
            {antecedenciaForaDoCatalogo && (
              <SelectOption value={String(config.antecedenciaMin)}>
                {rotuloMinutos(config.antecedenciaMin)}
              </SelectOption>
            )}
          </Select>
          <p className="text-sm text-ink-3 dark:text-ink-dark-3 mt-1.5">
            Antecedência padrão do aviso. As opções vêm da lista de tempos ao lado.
          </p>
        </Card>

        <TemposCatalogo tempos={tempos.data ?? []} />
      </div>

      <DefaultPorTipo temposAtivos={temposAtivos} />
    </div>
  );
}

// ─── CA-6: catálogo de tempos de lembrete (CRUD admin) ─────────────────

function TemposCatalogo(props: { tempos: LembreteTempo[] }) {
  const acoes = useConfigActions('lembrete-tempos');
  const toast = useToast();
  const [novoNome, setNovoNome] = useState('');
  const [novoMinutos, setNovoMinutos] = useState('');
  const [editando, setEditando] = useState<LembreteTempo | null>(null);
  const [nomeEditado, setNomeEditado] = useState('');

  async function criar() {
    const minutos = Number(novoMinutos);
    if (!novoNome.trim() || !Number.isInteger(minutos) || minutos < 1) {
      toast.show('Dê um nome e um número inteiro de minutos (1 ou mais).', { variant: 'error' });
      return;
    }
    const maiorOrdem = Math.max(0, ...props.tempos.map((t) => t.ordem));
    try {
      await acoes.criar.mutateAsync({ nome: novoNome.trim(), minutos, ordem: maiorOrdem + 1 });
      setNovoNome('');
      setNovoMinutos('');
      toast.show('Tempo adicionado.');
    } catch (err) {
      // I-1: nada garante minutos únicos no banco hoje — a frase antiga mandava
      // procurar um conflito que o schema não cria. Agora só aparece num 409 real.
      toast.show(mensagemErroConfig(err, 'Já existe um tempo com esses minutos.'), { variant: 'error' });
    }
  }

  return (
    <Card variant="flat" className="p-4 flex flex-col min-w-0">
      <h3 className="text-base font-bold text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
        Tempos de lembrete
        <HelpTip texto="A lista de tempos que aparece pra quem agenda uma atividade (ex.: 15 minutos, 1 hora, 1 dia antes). Desativar um tempo tira ele das escolhas novas sem mexer nos lembretes já marcados." />
      </h3>
      <p className="text-sm text-ink-3 dark:text-ink-dark-3 mt-0.5 mb-3">
        As opções de “quanto tempo antes avisar” oferecidas ao agendar uma atividade.
      </p>
      <div className="space-y-1.5 mb-3 max-h-72 overflow-y-auto pr-0.5">
        {props.tempos.map((t) => (
          // FASE-C: fileira quebra no celular e o "45 min" virou <Badge> — o
          // rótulo não gruda mais no nome do tempo.
          <div
            key={t.id}
            className={cn(
              'flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-lg border border-line-soft dark:border-line-soft-dark px-3 py-2',
              !t.ativo && 'opacity-50',
            )}
          >
            <span className="flex-1 min-w-0 break-words text-sm font-medium text-ink-1 dark:text-ink-dark-1">
              {t.nome}
            </span>
            <Badge variant="neutral" size="sm" className="shrink-0">
              {t.minutos} min
            </Badge>
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1 shrink-0">
              <button
                type="button"
                className="text-xs text-ink-3 dark:text-ink-dark-3 hover:text-red"
                onClick={() => {
                  setEditando(t);
                  setNomeEditado(t.nome);
                }}
              >
                renomear
              </button>
              <button
                type="button"
                data-testid={`cfg-lembrete-tempo-toggle-${t.id}`}
                className="text-xs text-ink-3 dark:text-ink-dark-3 hover:text-red"
                onClick={() =>
                  acoes.editar.mutate(
                    { id: t.id, ativo: !t.ativo },
                    {
                      onError: () =>
                        toast.show(
                          t.ativo ? 'Não foi possível desativar o tempo.' : 'Não foi possível reativar o tempo.',
                          { variant: 'error' },
                        ),
                    },
                  )
                }
              >
                {t.ativo ? 'desativar' : 'reativar'}
              </button>
            </span>
          </div>
        ))}
        {props.tempos.length === 0 && <p className="text-sm text-ink-3 dark:text-ink-dark-3">Lista vazia.</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        <Input
          size="sm"
          wrapperClassName="flex-1 min-w-[140px]"
          placeholder="Nome (ex.: 3 horas antes)…"
          value={novoNome}
          onChange={(e) => setNovoNome(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && criar()}
          data-testid="cfg-lembrete-tempo-novo-nome"
          aria-label="Nome do novo tempo"
        />
        <Input
          size="sm"
          type="number"
          min={1}
          step={1}
          inputMode="numeric"
          wrapperClassName="w-24 shrink-0"
          placeholder="Minutos"
          value={novoMinutos}
          onChange={(e) => setNovoMinutos(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && criar()}
          data-testid="cfg-lembrete-tempo-novo-minutos"
          aria-label="Minutos de antecedência do novo tempo"
        />
        <Button
          size="sm"
          variant="secondary"
          onClick={criar}
          disabled={!novoNome.trim() || !novoMinutos.trim()}
          data-testid="cfg-lembrete-tempo-adicionar"
        >
          Adicionar
        </Button>
      </div>

      <Dialog open={Boolean(editando)} onOpenChange={(o) => !o && setEditando(null)} size="sm">
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renomear tempo</DialogTitle>
          </DialogHeader>
          <Input value={nomeEditado} onChange={(e) => setNomeEditado(e.target.value)} aria-label="Novo nome do tempo" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditando(null)}>
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                if (!editando) return;
                try {
                  await acoes.editar.mutateAsync({ id: editando.id, nome: nomeEditado });
                  setEditando(null);
                } catch {
                  toast.show('Não foi possível renomear.', { variant: 'error' });
                }
              }}
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── CA-6: tempo padrão de lembrete por tipo de atividade ──────────────

function DefaultPorTipo(props: { temposAtivos: LembreteTempo[] }) {
  const tipos = useTiposAtividadeCrm();
  const acoes = useConfigActions('tipos-atividade');
  const toast = useToast();
  const lista = tipos.data ?? [];
  const nenhumComPadrao = lista.length > 0 && lista.every((t) => t.lembreteDefaultMinutos == null);

  return (
    <Card variant="flat" className="p-4 min-w-0">
      <h3 className="text-base font-bold text-ink-1 dark:text-ink-dark-1 mb-1 inline-flex items-center gap-1.5">
        Padrão por tipo de atividade
        <HelpTip texto="O tempo de lembrete que já vem sugerido ao agendar cada tipo de atividade (ex.: Visita já sugere 1 dia antes). Quem cria a atividade pode trocar na hora. Tipo sem padrão obriga a escolher um tempo." />
      </h3>
      <p className="text-sm text-ink-3 dark:text-ink-dark-3 mb-3">
        Sugestão automática de lembrete pra cada tipo. “Sem padrão” = quem agenda escolhe na hora.
      </p>
      {/* C-5: a função pode estar LIGADA E VAZIA — foi o que estava acontecendo,
          e ninguém percebia porque a tela não dizia nada. Enquanto nenhum tipo
          tiver padrão, ela avisa que o efeito prático é zero. */}
      {nenhumComPadrao && (
        <p
          className="mb-3 rounded-lg bg-warning/10 text-warning px-3 py-2 text-sm font-medium"
          data-testid="aviso-nenhum-padrao"
        >
          Nenhum tipo de atividade tem lembrete padrão. Enquanto não tiver, o vendedor cria a
          atividade sem lembrete pré-selecionado.
        </p>
      )}
      <div className="space-y-1.5">
        {(tipos.data ?? []).map((t) => {
          const foraDoCatalogo =
            t.lembreteDefaultMinutos != null &&
            !props.temposAtivos.some((x) => x.minutos === t.lembreteDefaultMinutos);
          return (
            <div
              key={t.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-line-soft dark:border-line-soft-dark px-3 py-2"
            >
              <span className="flex-1 min-w-0 break-words text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                {t.nome}
              </span>
              <Select
                size="sm"
                value={t.lembreteDefaultMinutos == null ? '' : String(t.lembreteDefaultMinutos)}
                onChange={(e) =>
                  acoes.editar.mutate(
                    { id: t.id, lembreteDefaultMinutos: e.target.value === '' ? null : Number(e.target.value) },
                    { onError: () => toast.show('Não foi possível salvar o padrão.', { variant: 'error' }) },
                  )
                }
                wrapperClassName="w-56 shrink-0"
                aria-label={`Lembrete padrão do tipo ${t.nome}`}
                data-testid={`cfg-lembrete-default-${t.id}`}
              >
                <SelectOption value="">Sem padrão (escolher na hora)</SelectOption>
                {props.temposAtivos.map((x) => (
                  <SelectOption key={x.id} value={String(x.minutos)}>
                    {x.nome}
                  </SelectOption>
                ))}
                {foraDoCatalogo && (
                  <SelectOption value={String(t.lembreteDefaultMinutos)}>
                    {rotuloMinutos(t.lembreteDefaultMinutos as number)}
                  </SelectOption>
                )}
              </Select>
            </div>
          );
        })}
        {(tipos.data ?? []).length === 0 && (
          <p className="text-sm text-ink-3 dark:text-ink-dark-3">
            Nenhum tipo de atividade ativo. Cadastre os tipos na aba “Listas do CRM”.
          </p>
        )}
      </div>
    </Card>
  );
}

export default LembreteConfigTab;
